import { Fragment, useState, useEffect, useCallback } from 'react';
import { Transition } from '@headlessui/react';
import { FaEllipsisV, FaTimes, FaPlus, FaEdit, FaTrash, FaUser } from 'react-icons/fa';
import { Menu, MenuItem } from '@mui/material';
import axios, { getBaseUrl } from '../../../services/api';
import { toast } from 'react-toastify';
import ConfirmModal from '../../../components/modals/ConfirmModal';
import AgentModal from './AgentModal';
import CategoryModal from './CategoryModal';
import { useTranslation } from 'react-i18next';
import { generateUUID } from '../../../services/utils';

export default function CallCenterModal({ isOpen, onClose, callCenter, onSuccess }) {
  const { t } = useTranslation();
  const [categories, setCategories] = useState([]);
  const [agents, setAgents] = useState([]);
  const [webrtcAccounts, setWebrtcAccounts] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [hoveredCategory, setHoveredCategory] = useState(null);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isConfirmAgentOpen, setIsConfirmAgentOpen] = useState(false);
  const [isAgentModalOpen, setIsAgentModalOpen] = useState(false);
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [defaultCallCenterId, setDefaultCallCenterId] = useState('');

  // Form state
  const [newCallCenter, setNewCallCenter] = useState({
    centerId: defaultCallCenterId,
    name: '',
    domain: `${window.location.protocol}${window.location.domin}`,
    centerUrl: '',
    webrtcAccount: '',
  });

  const [callCenterName, setCallCenterName] = useState('');
  const [domain, setDomain] = useState('https://');
  const [centerUrl, setCenterUrl] = useState('');
  const [webrtcAccount, setWebrtcAccount] = useState('');

  const handleAgentMenuClick = (event, agent) => {
    setAnchorEl(event.currentTarget);
    setSelectedAgent(agent);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const fetchCategories = async () => {
    try {
      if (newCallCenter?._id) {
        const response = await axios.get(
          `/callcenter-categories/center/${newCallCenter?._id || ''}`
        );
        setCategories(response.data || []);
      } else {
        setCategories([]);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchAgents = async () => {
    try {
      if (selectedCategory?._id) {
        const response = await axios.get(`/callcenter-agents/category/${selectedCategory?._id}`);
        setAgents(response.data || []);
      } else {
        setAgents([]);
      }
    } catch (error) {
      console.error('Error fetching agents:', error);
      setAgents([]);
    }
  };

  const fetchWebrtcAccounts = async () => {
    try {
      // const response = await axios.get(
      //   `/settings/unassigned-sips?sipAccount=${newCallCenter?.webrtcAccount}&type=webrtc`
      // );
      const response = await axios.get('/sip_account/accounts', {
        params: {
          category: 'webrtc',
          assignmentStatus: 'all',
          status: 'all',
          searchTerm: ''
        }
      });
      setWebrtcAccounts(response.data || []);
    } catch (error) {
      console.error('Error fetching WebRTC accounts:', error);
      setWebrtcAccounts([]);
    }
  };

  const handleAddCategory = useCallback(() => {
    if (!newCallCenter?._id) {
      toast.error(t('Please save the call center first'));
      return;
    }
    setSelectedCategory(null);
    setIsCategoryModalOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newCallCenter]);

  const handleEditCategory = category => {
    setSelectedCategory(category);
    setIsCategoryModalOpen(true);
    onSuccess();
  };

  const handleCategorySuccess = () => {
    fetchCategories();
    setIsCategoryModalOpen(false);
    onSuccess();
  };

  const handleDeleteCategory = category => {
    setSelectedCategory(category);
    setIsConfirmOpen(true);
  };

  const confirmDeleteCategory = async () => {
    try {
      await axios.delete(`/callcenter-categories/${selectedCategory?._id}`);
      toast.success(t('Delete Successfully'));
      setSelectedCategory(null);
      fetchCategories();
      // eslint-disable-next-line react-hooks/exhaustive-deps
      setIsConfirmOpen(false);
      onSuccess();
    } catch (error) {
      console.error('Error deleting category:', error);
      toast.error(t('Delete Failed'));
    }
  };

  const handleAddAgent = () => {
    if (!selectedCategory) {
      toast.error(t('Please select a category first'));
      // eslint-disable-next-line react-hooks/exhaustive-deps
      return;
    }
    setSelectedAgent(null);
    setIsAgentModalOpen(true);
  };

  const handleEditAgent = () => {
    handleMenuClose();
    setIsAgentModalOpen(true);
  };

  const handleDeleteAgent = () => {
    handleMenuClose();
    setIsConfirmAgentOpen(true);
  };

  const confirmDeleteAgent = async () => {
    setIsConfirmAgentOpen(false);
    try {
      await axios.delete(`/callcenter-agents/${selectedAgent._id}`);
      toast.success(t('Delete Successfully'));
      fetchAgents();
      setIsAgentModalOpen(false);
      setSelectedAgent(null);
      onSuccess();
    } catch (error) {
      console.error('Error deleting agent:', error);
      toast.error(t('Delete Failed'));
      setIsConfirmAgentOpen(false);
    }
  };

  const handleAgentSuccess = () => {
    fetchAgents();
    setIsAgentModalOpen(false);
    onSuccess();
  };

  const handleSave = async () => {
    // Validation
    if (!webrtcAccount || !domain || !centerUrl || !callCenterName) {
      toast.error(t('Please input all required fields'));
      return;
    }

    if (!domain.startsWith('https://') && !domain.startsWith('http://')) {
      toast.error(t('Please input valid domain'));
      return;
    }

    const data = {
      name: callCenterName,
      domain: domain,
      url: centerUrl,
      webrtcAccount: webrtcAccount,
    };

    try {
      if (newCallCenter) {
        const response = await axios.put(`/callcenters/${newCallCenter._id}`, {
          ...data,
          centerId: newCallCenter.centerId,
        });
        setNewCallCenter(response.data);
        toast.success(t('Update Successfully'));
      } else {
        const response = await axios.post('/callcenters', {
          ...data,
          centerId: defaultCallCenterId,
        });
        setNewCallCenter(response.data);
        toast.success(t('Save Successfully'));
      }
      onSuccess();
    } catch (error) {
      console.error('Error saving call center:', error);
      // toast.error(t('Save Failed'));
    }
  };

  useEffect(() => {
    if (isOpen) {
      setSelectedCategory(null);
      setNewCallCenter(callCenter);
      setDefaultCallCenterId(generateUUID());
    }
  }, [isOpen, callCenter]);

  useEffect(() => {
    fetchWebrtcAccounts();
    fetchCategories();
    fetchAgents();

    setCallCenterName(newCallCenter?.name || '');
    setDomain(newCallCenter?.domain || `${window.location.protocol}//${window.location.host}`);
    setCenterUrl(
      newCallCenter?.url ||
        `callcenter?id=${newCallCenter?.centerId ? newCallCenter?.centerId : defaultCallCenterId}`
    );
    setWebrtcAccount(newCallCenter?.webrtcAccount || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newCallCenter, defaultCallCenterId]);

  useEffect(() => {
    if (categories.length > 0) {
      if (!selectedCategory) setSelectedCategory(categories[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categories]);

  useEffect(() => {
    fetchAgents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory]);

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center pt-6 z-50 app-text">
        <div className="bg-white w-[60vw] h-[85vh] rounded-lg shadow-xl flex flex-col">
          <div className="bg-black text-white px-4 py-3 rounded-t-lg flex justify-between items-center">
            <h2 className="text-lg font-semibold">{t('sidebar.callCenter')}</h2>
            <button
              onClick={() => {
                setNewCallCenter(null);
                setSelectedCategory(null);
                setCategories([]);
                setAgents([]);
                setWebrtcAccounts([]);
                onClose();
              }}
              className="text-white/70 hover:text-white transition-colors"
            >
              <FaTimes />
            </button>
          </div>

          <div className="flex-1 overflow-hidden flex flex-col">
            <div className="px-6 pt-4 pb-3">
              <div className="space-y-3">
                <div className="flex gap-4">
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('Call Center Name')}
                      <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={callCenterName}
                      onChange={e => setCallCenterName(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div className="w-[200px]">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('WebRTC Account')}
                      <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={webrtcAccount}
                      onChange={e => setWebrtcAccount(e.target.value)}
                      className="w-full h-[34px] px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                    >
                      <option value="">{t('Select Webrtc Account')}</option>
                      {webrtcAccounts.map(account => (
                        <option key={account._id} value={account.username}>
                          {account.username}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('Access Domain Name')}
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={domain}
                      onChange={e => setDomain(e.target.value)}
                      className="w-48 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      // disabled
                    />
                    <input
                      type="text"
                      value={centerUrl}
                      onChange={e => setCenterUrl(e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder=""
                      disabled
                    />
                    <button
                      onClick={handleSave}
                      className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors whitespace-nowrap"
                    >
                      {t('Save')}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex-1 flex gap-3 overflow-hidden px-6 pb-3">
              <div className="w-1/4 flex flex-col">
                <div className="p-1 bg-gray-50 flex items-center justify-between">
                  <h3 className="font-semibold text-gray-700">{t('Category List')}</h3>
                  <button
                    onClick={handleAddCategory}
                    className="p-1 text-blue-500 hover:bg-blue-50 rounded transition-colors"
                  >
                    <FaPlus size={14} />
                  </button>
                </div>

                <div className="flex-1 border rounded-md overflow-y-auto">
                  {categories.map(category => (
                    <div
                      key={category._id}
                      className={`px-4 py-3 border-b cursor-pointer flex items-center justify-between group ${
                        selectedCategory?._id === category._id ? 'bg-green-100' : 'hover:bg-gray-50'
                      }`}
                      onMouseEnter={() => setHoveredCategory(category._id)}
                      onMouseLeave={() => setHoveredCategory(null)}
                      onClick={() => setSelectedCategory(category)}
                    >
                      {/* <span className="flex items-center text-gray-700 h-[14px]">{category.name}</span> */}
                      <div className="max-w-[100px] truncate h-[16px]" title={category.name}>
                        {category.name}
                      </div>
                      {hoveredCategory === category._id && (
                        <div className="flex items-center gap-4">
                          <button
                            onClick={e => {
                              e.stopPropagation();
                              handleEditCategory(category);
                            }}
                            className="text-blue-500 hover:bg-blue-50 rounded"
                          >
                            <FaEdit size={14} />
                          </button>
                          <button
                            onClick={e => {
                              e.stopPropagation();
                              handleDeleteCategory(category);
                            }}
                            className="text-red-500 hover:bg-red-50 rounded"
                          >
                            <FaTrash size={14} />
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex-1 flex flex-col overflow-hidden">
                <div className="p-1 bg-gray-50 flex items-center justify-between">
                  <h3 className="font-semibold text-gray-700">{t('Agent List')}</h3>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={handleAddAgent}
                      className="p-1 text-blue-500 hover:bg-blue-50 rounded transition-colors"
                    >
                      <FaPlus size={14} />
                    </button>
                  </div>
                </div>
                <div className="flex-1 border rounded-md overflow-auto">
                  <table className="w-full">
                    <thead className="sticky top-0 bg-gray-100 border-b">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">
                          {t('No')}
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">
                          {t('Avatar')}
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">
                          {t('Name')}
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">
                          {t('Gender')}
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">
                          {t('email')}
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">
                          {t('Sip Account')}
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">
                          {t('isPublic')}
                        </th>
                        <th className="px-4 py-2 text-center text-xs font-semibold text-gray-600">
                          {t('Actions')}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {agents.length === 0 ? (
                        <tr>
                          <td colSpan="8" className="px-4 py-8 text-center text-gray-400">
                            {t('No result')}
                          </td>
                        </tr>
                      ) : (
                        agents.map((agent, index) => (
                          <tr key={agent._id} className="border-b hover:bg-gray-50">
                            <td className="px-2 py-3 text-sm text-center">{index + 1}</td>
                            <td className="px-4 py-3">
                              <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden">
                                {agent.avatar ? (
                                  <img
                                    src={
                                      agent.avatar.startsWith('http') ||
                                      agent.avatar.startsWith('data:')
                                        ? agent.avatar
                                        : `${getBaseUrl()}${agent.avatar}`
                                    }
                                    alt={agent.name}
                                    className="w-full h-full object-cover"
                                    onError={e => {
                                      e.target.style.display = 'none';
                                      e.target.parentElement.innerHTML =
                                        '<svg class="text-gray-400" width="14" height="14" fill="currentColor" viewBox="0 0 448 512"><path d="M224 256c70.7 0 128-57.3 128-128S294.7 0 224 0 96 57.3 96 128s57.3 128 128 128zm89.6 32h-16.7c-22.2 10.2-46.9 16-72.9 16s-50.6-5.8-72.9-16h-16.7C60.2 288 0 348.2 0 422.4V464c0 26.5 21.5 48 48 48h352c26.5 0 48-21.5 48-48v-41.6c0-74.2-60.2-134.4-134.4-134.4z"></path></svg>';
                                    }}
                                  />
                                ) : (
                                  <FaUser className="text-gray-400" size={14} />
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-3 text-sm">
                              <div className="max-w-[80px] truncate" title={agent.name}>
                                {agent.name}
                              </div>
                            </td>
                            <td className="px-4 py-3 text-sm">
                              <div
                                className="max-w-[80px] truncate"
                                title={agent.gender ? t(agent.gender) : '--'}
                              >
                                {agent.gender ? t(agent.gender) : '--'}
                              </div>
                            </td>
                            <td className="px-4 py-3 text-sm">
                              <div className="max-w-[80px] truncate" title={agent.email || '--'}>
                                {agent.email || '--'}
                              </div>
                            </td>
                            <td className="px-4 py-3 text-sm">
                              <div
                                className="max-w-[80px] truncate"
                                title={agent.sipAccount || '--'}
                              >
                                {agent.sipAccount || '--'}
                              </div>
                            </td>
                            <td className="px-4 py-3 text-sm">
                              <div
                                className="max-w-[80px] truncate"
                                title={agent.publicStatus === 'public' ? t('Yes') : t('no')}
                              >
                                {agent.publicStatus === 'public' ? t('Yes') : t('no')}
                              </div>
                            </td>
                            <td className="px-4 py-3 text-center">
                              <button
                                onClick={e => handleAgentMenuClick(e, agent)}
                                className="text-gray-600 hover:text-gray-900"
                              >
                                <FaEllipsisV />
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          className: 'shadow-lg rounded-lg',
          elevation: 0,
        }}
      >
        <MenuItem onClick={handleEditAgent} className="text-sm px-4 py-2 hover:bg-blue-50">
          {t('Edit')}
        </MenuItem>

        <MenuItem
          onClick={handleDeleteAgent}
          className="text-sm px-4 py-2 hover:bg-red-50 text-red-600"
        >
          {t('Delete')}
        </MenuItem>
      </Menu>

      <ConfirmModal
        isOpen={isConfirmOpen}
        onClose={() => setIsConfirmOpen(false)}
        onConfirm={confirmDeleteCategory}
        title={t('Delete Category')}
        message={
          agents?.length > 0
            ? t(
                'This category has agents assigned to it. Are you sure you want to delete it? All agents will be deleted from this category.'
              )
            : t('Are you sure you want to delete this category? This action cannot be undone.')
        }
        confirmText={t('Delete')}
        cancelText={t('Cancel')}
        type="danger"
      />

      <ConfirmModal
        isOpen={isConfirmAgentOpen}
        onClose={() => setIsConfirmAgentOpen(false)}
        onConfirm={confirmDeleteAgent}
        title={t('Delete Agent')}
        message={t('Are you sure you want to delete this agent? This action cannot be undone.')}
        confirmText={t('Delete')}
        cancelText={t('Cancel')}
        type="danger"
      />

      <AgentModal
        isOpen={isAgentModalOpen}
        onClose={() => {
          setIsAgentModalOpen(false);
          setSelectedAgent(null);
        }}
        agent={selectedAgent}
        categoryId={selectedCategory?._id || ''}
        centerId={newCallCenter?._id || ''}
        onSuccess={handleAgentSuccess}
      />

      <CategoryModal
        isOpen={isCategoryModalOpen}
        onClose={() => {
          setIsCategoryModalOpen(false);
        }}
        category={selectedCategory}
        callCenterId={newCallCenter?._id || ''}
        onSuccess={handleCategorySuccess}
      />
    </Transition>
  );
}
